import {
  Box,
  Heading,
  VStack,
  StackDivider,
  Button,
  Text,
  Input,
  Grid,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  useColorModeValue,
} from '@chakra-ui/react'
import { useQuery, gql, useMutation } from '@apollo/client'
import { useNavigate, useParams } from 'react-router-dom'
import { FaEdit } from 'react-icons/fa'
import { useState } from 'react'
import { Buildpacks } from '../../../components'

const SETTINGS_QUERY = gql`
  query Settings($appName: String!) {
    app(name: $appName) {
      id
      displayName
      repoUrl
      defaultBranch
      buildPacks {
        id
        source
        order
      }
    }
  }
`

const DELETE_APP_MUTATION = gql`
  mutation ($appName: String!) {
    deleteApp(name: $appName)
  }
`

const Settings = () => {
  const borderColor = useColorModeValue('gray.300', 'whiteAlpha.300')
  const { name } = useParams()
  const navigate = useNavigate()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isAddBuildpackModalOpen, setIsAddBuildpackModalOpen] = useState(false)

  const { data, refetch } = useQuery(SETTINGS_QUERY, {
    variables: { appName: name },
  })
  const [deleteApp, { loading: isDeletingApp }] = useMutation(
    DELETE_APP_MUTATION,
    {
      variables: { appName: name },
    }
  )

  return (
    <>
      <Box p={4} rounded="lg" border="1px" borderColor={borderColor}>
        <VStack divider={<StackDivider />} spacing={4} align="stretch">
          <Grid
            gridTemplateColumns="0.65fr 1fr 40px"
            gap={8}
            alignItems="center"
          >
            <VStack align="start" py={6}>
              <Heading as="h2" size="md">
                App Name
              </Heading>
              <Text>A unique name of your application.</Text>
            </VStack>
            <Input value={data?.app?.displayName ?? ''} isDisabled />
            <IconButton
              aria-label="Edit application name"
              isDisabled
              icon={<FaEdit />}
            />
          </Grid>
          <Grid
            gridTemplateColumns="0.65fr 1fr 40px"
            gap={8}
            alignItems="center"
          >
            <VStack align="start" py={6}>
              <Heading as="h2" size="md">
                Repository URL
              </Heading>
              <Text>The URL of your application's GitHub repository.</Text>
            </VStack>
            <Input value={data?.app?.repoUrl ?? ''} isDisabled />
            <IconButton
              aria-label="Edit URL of your application's repository"
              isDisabled
              icon={<FaEdit />}
            />
          </Grid>
          <Grid
            gridTemplateColumns="0.65fr 1fr 40px"
            gap={8}
            alignItems="center"
          >
            <VStack align="start" py={6}>
              <Heading as="h2" size="md">
                Default Branch
              </Heading>
              <Text>
                Default branch of your application's GitHub repository.
              </Text>
            </VStack>
            <Input value={data?.app?.defaultBranch ?? ''} isDisabled />
            <IconButton
              aria-label="Edit the default branch of your application's repository"
              isDisabled
              icon={<FaEdit />}
            />
          </Grid>
          <Grid gridTemplateColumns="0.65fr 1fr" alignItems="center" py={6}>
            <VStack align="start">
              <Heading as="h2" size="md">
                Buildpacks
              </Heading>
              <Text pr={8}>
                Buildpacks are scripts that are run when your app is deployed.
                They are used to install dependencies for your app and configure
                your environment.
              </Text>
            </VStack>
            <VStack alignItems="flex-end" justifyContent="stretch">
              <Button
                alignSelf="end"
                size="sm"
                colorScheme="cyan"
                variant="outline"
                onClick={() => setIsAddBuildpackModalOpen(true)}
              >
                Add buildpack
              </Button>
              {/* @ts-ignore */}
              <Buildpacks
                buildpacks={data?.app?.buildPacks ?? []}
                refetchData={refetch}
                addModal={{
                  isOpen: isAddBuildpackModalOpen,
                  onClose: () => setIsAddBuildpackModalOpen(false),
                }}
              />
            </VStack>
          </Grid>
          <Grid gridTemplateColumns="0.65fr 1fr" alignItems="center" py={6}>
            <VStack align="start">
              <Heading as="h2" size="md" color="red.400">
                Danger Zone
              </Heading>
              <Text>
                This is a dangerous operation. Please remember that deleting
                your application is irreversible and data may be lost.
              </Text>
            </VStack>
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              justifySelf="end"
              onClick={() => setIsDeleteModalOpen(true)}
            >
              Delete {data?.app?.displayName ?? 'app'}
            </Button>
          </Grid>
        </VStack>
      </Box>
      <Modal
        isOpen={isDeleteModalOpen}
        isCentered
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Application</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you'd like to delete{' '}
            {data?.app?.displayName ?? 'your app'}?
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => setIsDeleteModalOpen(false)}
            >
              No
            </Button>
            <Button
              colorScheme="red"
              variant="solid"
              onClick={async () => {
                await deleteApp()
                navigate('/')
              }}
              isLoading={isDeletingApp}
            >
              Yes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default Settings
